import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowUpRight, Check, MousePointer2, Pencil, RotateCcw, Square, Type, X } from "lucide-react";

const COLORS = ["#F59E0B", "#EF4444", "#FFFFFF", "#10B981", "#0EA5E9", "#18181B"];
const TOOLS = [["arrow", ArrowUpRight, "Arrow"], ["pen", Pencil, "Draw"], ["box", Square, "Box"], ["text", Type, "Note"]];
const usePhone = () => {
  const [m, setM] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => { const mq = window.matchMedia("(max-width: 767px)"); const h = (e) => setM(e.matches); mq.addEventListener("change", h); return () => mq.removeEventListener("change", h); }, []);
  return m;
};

// PHOTO ANNOTATOR — arrows, boxes, freehand and notes on a site photo before it goes to the customer. Exports a flattened JPEG.
// Phones get a full-screen stage (portal), one-finger drawing with pointer capture, and a note bar that works with the on-screen keyboard.
export function PhotoAnnotator({ src, filename = "photo.jpg", onSave, onCancel, saving }) {
  const phone = usePhone();
  const canvasRef = useRef();
  const imgRef = useRef();
  const noteInput = useRef();
  const wrapRef = useRef();
  const pointerId = useRef(null);
  const tap = useRef(null);
  const draftRef = useRef(null);
  const [shapes, setShapes] = useState([]);
  const [draft, setDraftState] = useState(null);
  const setDraft = (d) => { draftRef.current = d; setDraftState(d); };
  const [tool, setTool] = useState("arrow");
  const [color, setColor] = useState(COLORS[0]);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState("");
  const [noteAt, setNoteAt] = useState(null);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { imgRef.current = img; const c = canvasRef.current; if (!c) return; const scale = Math.min(1, 1600 / img.width); c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale); setReady(true); };
    img.onerror = () => setFailed(true);
    img.src = src;
  }, [src]);

  useEffect(() => { if (!phone) return; const prev = document.body.style.overflow; document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = prev; }; }, [phone]);

  // Full-screen: size the canvas to fill the stage while keeping the photo's aspect ratio (no object-fit, so pointer maths stay exact).
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    if (!phone || !ready) { c.style.width = ""; c.style.height = ""; return; }
    const fit = () => { const box = wrapRef.current?.getBoundingClientRect(); if (!box) return; const s = Math.min(box.width / c.width, box.height / c.height); c.style.width = `${Math.floor(c.width * s)}px`; c.style.height = `${Math.floor(c.height * s)}px`; };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [phone, ready, tool]);

  const textSize = () => Math.max(18, Math.round(canvasRef.current.width / 28));
  const preview = noteAt && note.trim() ? [{ type: "text", color, x: noteAt.x, y: noteAt.y, text: note.trim(), size: textSize() }] : [];
  useEffect(() => { if (ready) paint(canvasRef.current, imgRef.current, [...shapes, ...(draft ? [draft] : []), ...preview]); }, [shapes, draft, ready, note, noteAt, color]); // eslint-disable-line react-hooks/exhaustive-deps

  const pos = (e) => { const c = canvasRef.current, r = c.getBoundingClientRect(); return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) }; };
  const lw = () => Math.max(3, Math.round(canvasRef.current.width / 220));
  const centre = () => ({ x: canvasRef.current.width / 2, y: canvasRef.current.height / 2 });
  const placeNote = (p, value) => { if (!value.trim()) return; setShapes((prev) => [...prev, { type: "text", color, x: p.x, y: p.y, text: value.trim(), size: textSize() }]); setNote(""); setNoteAt(null); };

  const down = (e) => {
    if (!ready) return;
    if (pointerId.current != null && e.pointerId !== pointerId.current) return; // a second finger never restarts the stroke
    e.preventDefault();
    const p = pos(e);
    pointerId.current = e.pointerId;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* synthetic pointer */ }
    if (tool === "text") { tap.current = p; return; }
    setDraft(tool === "pen" ? { type: "pen", color, w: lw(), pts: [p] } : { type: tool, color, w: lw(), x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };
  const move = (e) => {
    if (e.pointerId !== pointerId.current) return;
    const d = draftRef.current; if (!d) return;
    const p = pos(e);
    setDraft(d.type === "pen" ? { ...d, pts: [...d.pts, p] } : { ...d, x2: p.x, y2: p.y });
  };
  const up = (e) => {
    if (e.pointerId !== pointerId.current) return;
    pointerId.current = null;
    if (tool === "text" && tap.current) {
      const p = e.type === "pointerup" ? pos(e) : tap.current; tap.current = null;
      if (note.trim()) placeNote(p, note); else { setNoteAt(p); noteInput.current?.focus(); }
      return;
    }
    const d = draftRef.current; if (!d) return;
    if (d.type !== "pen" && e.type === "pointerup") { const p = pos(e); d.x2 = p.x; d.y2 = p.y; }
    const big = d.type === "pen" ? d.pts.length > 2 : Math.hypot(d.x2 - d.x1, d.y2 - d.y1) > 6;
    if (big) setShapes((prev) => [...prev, d]);
    setDraft(null);
  };
  const save = () => canvasRef.current.toBlob((blob) => onSave(new File([blob], filename.replace(/\.[^.]+$/, "") + "-marked.jpg", { type: "image/jpeg" }), shapes.length), "image/jpeg", 0.9);
  const pick = (t) => { setTool(t); if (t !== "text") setNoteAt(null); };

  const hint = tool === "text" ? (noteAt ? "Type the note, then press Place" : note.trim() ? "Tap the photo where the note should go" : "Type a note, then tap the photo") : tool === "pen" ? "Draw with one finger" : `Drag to place ${tool === "arrow" ? "an arrow" : "a box"}`;
  const tb = phone ? "!h-11 !w-11" : "";
  const c = canvasRef.current;

  const body = (
    <div className={phone ? "anno-dark fixed inset-0 z-[80] flex flex-col bg-zinc-950 text-white" : "space-y-3"} data-testid="photo-annotator" data-mode={phone ? "fullscreen" : "inline"}>
      <div className={`flex flex-wrap items-center gap-2 ${phone ? "px-3 pb-2 pt-[calc(8px+env(safe-area-inset-top))]" : ""}`}>
        <div className="flex gap-1" role="toolbar" aria-label="Tools">{TOOLS.map(([t, Icon, l]) => <button key={t} type="button" className={`anno-tool ${tb} ${tool === t ? "on" : ""}`} onClick={() => pick(t)} title={l} aria-label={l} data-testid={`anno-tool-${t}`}><Icon size={phone ? 18 : 16} /></button>)}</div>
        <div className="flex items-center gap-1.5 pl-1">{COLORS.map((col) => <button key={col} type="button" className={`anno-swatch ${phone ? "!h-7 !w-7" : ""} ${color === col ? "on" : ""}`} style={{ background: col }} onClick={() => setColor(col)} aria-label={col} data-testid={`anno-color-${col.slice(1)}`} />)}</div>
        <div className="ml-auto flex gap-1">
          <button type="button" className={`anno-tool ${tb}`} onClick={() => setShapes(shapes.slice(0, -1))} disabled={!shapes.length} title="Undo" data-testid="anno-undo"><RotateCcw size={15} /></button>
          <button type="button" className={`anno-tool ${tb}`} onClick={() => setShapes([])} disabled={!shapes.length} title="Clear all" data-testid="anno-clear"><X size={15} /></button>
          {phone && <button type="button" className={`anno-tool ${tb}`} onClick={onCancel} disabled={saving} title="Close" aria-label="Close" data-testid="anno-close"><X size={18} /></button>}
        </div>
      </div>
      <div ref={wrapRef} className={phone ? "flex min-h-0 flex-1 items-center justify-center px-2 py-1" : ""}>
        <div className={`anno-stage ${phone ? "anno-fit" : ""}`} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} data-testid="anno-stage" data-tool={tool}>
          <canvas ref={canvasRef} data-testid="anno-canvas" />
          {noteAt && c && <span className="anno-pin" style={{ left: `${(noteAt.x / c.width) * 100}%`, top: `${(noteAt.y / c.height) * 100}%`, borderColor: color }} data-testid="anno-note-pin" />}
          {!ready && <div className="absolute inset-0 grid place-items-center text-[12px] text-zinc-400" data-testid="anno-loading">{failed ? "Couldn't load this photo — close and try again." : "Loading photo…"}</div>}
        </div>
      </div>
      {tool === "text" && (
        <div className={`flex items-center gap-2 ${phone ? "px-3 pt-2" : ""}`} data-testid="anno-note-bar">
          <input ref={noteInput} className={`input !py-2 text-[14px] ${phone ? "!border-zinc-700 !bg-zinc-900 !text-white placeholder:!text-zinc-500" : ""}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder={noteAt ? "Type the note…" : "Type a note, then tap the photo to place it"} enterKeyHint="done"
            onKeyDown={(e) => { if (e.key === "Enter") placeNote(noteAt || centre(), note); if (e.key === "Escape") { setNoteAt(null); setNote(""); } }} data-testid="anno-text-input" />
          <button type="button" className="btn-primary btn-sm shrink-0" disabled={!note.trim()} onClick={() => placeNote(noteAt || centre(), note)} data-testid="anno-note-place">Place</button>
        </div>
      )}
      <div className={`flex flex-wrap items-center justify-between gap-2 text-[12px] ${phone ? "px-3 pb-[calc(10px+env(safe-area-inset-bottom))] pt-2" : ""}`}>
        <span className={`flex items-center gap-1.5 ${phone ? "text-zinc-400" : "t3"}`}><MousePointer2 size={12} /> {hint}</span>
        <span className={`tnum font-semibold ${phone ? "text-white" : "t1"}`} data-testid="anno-count">{shapes.length ? `${shapes.length} mark${shapes.length > 1 ? "s" : ""}` : "No marks yet"}</span>
        <div className={`flex gap-2 ${phone ? "w-full" : ""}`}>
          {!phone && <button type="button" className="btn-secondary btn-sm" onClick={onCancel} disabled={saving} data-testid="anno-cancel">Cancel</button>}
          {phone && <button type="button" className="btn-secondary flex-1 !py-3" onClick={onCancel} disabled={saving} data-testid="anno-cancel">Cancel</button>}
          <button type="button" className={`btn-primary ${phone ? "flex-[2] !py-3" : "btn-sm"}`} onClick={save} disabled={!ready || saving} data-testid="anno-save"><Check size={13} /> {saving ? "Sending…" : shapes.length ? "Save marked-up photo" : "Use photo as is"}</button>
        </div>
      </div>
    </div>
  );
  return phone ? createPortal(body, document.body) : body;
}

function paint(canvas, img, shapes) {
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  for (const s of shapes) {
    ctx.strokeStyle = s.color; ctx.fillStyle = s.color; ctx.lineWidth = s.w || 4;
    ctx.shadowColor = "rgba(0,0,0,.45)"; ctx.shadowBlur = 6;
    if (s.type === "pen") { ctx.beginPath(); s.pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke(); }
    else if (s.type === "box") { ctx.strokeRect(Math.min(s.x1, s.x2), Math.min(s.y1, s.y2), Math.abs(s.x2 - s.x1), Math.abs(s.y2 - s.y1)); }
    else if (s.type === "arrow") {
      const a = Math.atan2(s.y2 - s.y1, s.x2 - s.x1), h = (s.w || 4) * 4;
      ctx.beginPath(); ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2 - Math.cos(a) * h * 0.6, s.y2 - Math.sin(a) * h * 0.6); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(s.x2, s.y2); ctx.lineTo(s.x2 - h * Math.cos(a - Math.PI / 7), s.y2 - h * Math.sin(a - Math.PI / 7)); ctx.lineTo(s.x2 - h * Math.cos(a + Math.PI / 7), s.y2 - h * Math.sin(a + Math.PI / 7)); ctx.closePath(); ctx.fill();
    } else if (s.type === "text") {
      ctx.font = `700 ${s.size}px -apple-system, BlinkMacSystemFont, Inter, sans-serif`; ctx.textBaseline = "middle";
      const w = ctx.measureText(s.text).width, pad = s.size * 0.35;
      ctx.shadowBlur = 0; ctx.fillStyle = "rgba(9,9,11,.72)"; ctx.beginPath(); ctx.roundRect(s.x - pad, s.y - s.size * 0.75, w + pad * 2, s.size * 1.5, s.size * 0.3); ctx.fill();
      ctx.fillStyle = s.color; ctx.fillText(s.text, s.x, s.y);
    }
    ctx.shadowBlur = 0;
  }
}
